import type AwcAutocompleteElement from '@ambiki/impulse-view-components/dist/elements/autocomplete';
import type { AwcAutocompleteCommitEvent } from '@ambiki/impulse-view-components/dist/elements/autocomplete';
import { Controller } from '@hotwired/stimulus';
import axios from 'axios';
import type EventLocationSelect from 'calendar/event_location_select_element';
import { cancel } from 'helpers/dom-helper';
import { parseJSON } from 'helpers/string';
import { nextFrame } from 'helpers/timing';
import dayjs from 'lib/dayjs';
import isEqual from 'lodash.isequal';
import useResetForm from 'stimulus-hooks/use-reset-form';

enum EventType {
  Therapy = '75957118-d9d3-422d-913c-2cdde72062d8',
}

export default class extends Controller<HTMLFormElement> {
  static values = {
    startTime: String,
    allDayEnabled: Boolean,
    authorizationVerificationUrl: String,
    formType: {
      default: 'events-new-form',
      type: String,
    },
  };

  static targets = [
    'allDayModeCheckbox',
    'durationContainer',
    'startTimeTimeContainer',
    'endTimeDateContainer',
    'timezoneContainer',
    'whenDatePicker',
    'untilDatePicker',
    'submitBtn',
    'rruleSelect',
    'authorizationWarning',
    'cancelEventCreation',
    'authorizationChecker',
    'authorizationWarningMessage',
    'eventForm',
    'issuesCount',
    'savingState',
    'eventLocationSelect',
  ];

  rrule = '';

  async connect() {
    useResetForm(this);
    if (this.allDayEnabledValue) {
      this.toggleAllDayFields();
    }
  }

  // Store selected element's rrule value.
  rruleSelectTargetConnected(target: AwcAutocompleteElement) {
    this.rrule = target.value as string;
  }

  // Clear rrule if select is removed from the DOM.
  rruleSelectTargetDisconnected() {
    this.rrule = '';
  }

  toggleAllDayFields(): void {
    this.durationContainerTarget.toggleAttribute('hidden', this.allDayModeCheckboxTarget.checked);
    this.startTimeTimeContainerTarget.toggleAttribute('hidden', this.allDayModeCheckboxTarget.checked);
    this.endTimeDateContainerTarget.toggleAttribute('hidden', !this.allDayModeCheckboxTarget.checked);
    this.untilDatePickerTarget.required = this.allDayModeCheckboxTarget.checked;
  }

  updateEndDatePicker(event: Event) {
    const datefield = event.target;
    if (!(datefield instanceof HTMLInputElement)) return;

    if (dayjs(datefield.value).isAfter(dayjs(this.untilDatePickerTarget.value))) {
      this.untilDatePickerTarget.value = datefield.value.toString();
    }
  }

  startDateChange(event: Event) {
    const datefield = event.target;
    if (!(datefield instanceof HTMLInputElement)) return;
    const sameDay = dayjs(this.startTimeValue).isSame(dayjs(datefield.value), 'day');
    // Set an attribute so that we can listen to changes on the `skd_event_update_type_controller.ts`
    // If day changes, we need to hide the 'All events in the series' update type.
    this.updateTypeModal?.setAttribute('data-skd-event-update-type-same-day-value', sameDay.toString());
  }

  rruleChange(event: CustomEvent<AwcAutocompleteCommitEvent>) {
    const { target } = event.detail;
    const value = target.getAttribute('value') || '';

    const isSame = isRRuleEqual(value, this.rrule);
    this.updateTypeModal?.setAttribute('data-skd-event-update-type-same-rrule-value', isSame.toString());
  }

  showUpdateTypeModal(event: SubmitEvent) {
    if (!event.submitter) return; // If we submit programmatically, then do nothing

    cancel(event);
    if (!this.updateTypeModal) return;

    this.updateTypeModal.setAttribute('data-form-id', this.element.id);
    $(this.updateTypeModal).modal('show');
  }

  requestSubmit(event: CustomEvent) {
    const { updateType, targetId } = event.detail;
    if (targetId !== this.element.id) return;

    // Show saving state before submitting the form at the earliest.
    this.showSavingState();

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'event[update_type]';
    input.value = updateType;
    this.element.append(input);

    this.element.requestSubmit();
  }

  // New method to show saving state
  showSavingState() {
    this.savingStateTarget.hidden = false;
  }

  // New method to hide saving state
  hideSavingState() {
    this.savingStateTarget.hidden = true;
  }

  enableSubmitBtn() {
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = false;
    }
  }

  disableSubmitBtn() {
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true;
    }
  }

  async resetForm() {
    await nextFrame();

    // Hide saving state and show form again
    this.hideSavingState();

    if (this.allDayEnabledValue) this.toggleAllDayFields();
    if (this.formTypeValue === 'events-new-form') {
      this.timezoneContainerTarget.setAttribute('data-reveal-index-value', '1'); // hide timezone container and show "View time zone" button
    }
    this.updateTypeModal?.removeAttribute('data-skd-event-update-type-same-rrule-value');

    if (this.hasEventLocationSelectTarget) {
      this.eventLocationSelectTarget.reset();
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private get updateTypeModal() {
    return document.getElementById('update-type-event');
  }

  showAuthorizationWarning(message: string, numberOfIssues: number) {
    const issuesCount = `${numberOfIssues} Authorization ${numberOfIssues > 1 ? 'issues' : 'issue'} found`;
    this.issuesCountTarget.textContent = issuesCount;
    this.authorizationWarningMessageTarget.innerHTML = message;
    this.authorizationWarningTarget.classList.remove('d-none');
    this.authorizationWarningTarget.classList.add('d-block');
  }

  closeAuthorizationWarning() {
    this.authorizationWarningTarget.classList.remove('d-block');
    this.authorizationWarningTarget.classList.add('d-none');
  }

  cancelAuthorizationWarning() {
    this.closeAuthorizationWarning();
    if (this.formTypeValue === 'events-new-form') {
      this.cancelEventCreationTarget.click();
    }
  }

  continueSave() {
    this.addAuthorizationCheck();
    this.closeAuthorizationWarning();

    // Show saving state before submitting after authorization
    this.showSavingState();

    this.submitBtnTarget.click();
  }

  async verifyAuthorization(event: SubmitEvent) {
    const formData = new FormData(this.eventFormTarget);
    const cleanFormData = this.removeEmptyKey(formData, 'event[type_id]'); // sometimes we get duplicate keys for event[type_id].

    // check if the authorization checker has been disabled or if its not a therapy event!
    if (cleanFormData.get('event[type_id]') === EventType.Therapy && !this.authorizationCheckerTarget.checked) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.eventFormTarget.checkValidity() && cleanFormData.get('event[type_id]') === EventType.Therapy) {
      if (!this.authorizationCheckerTarget.checked) {
        await this.checkAuthorizationValidation(formData);
      } else {
        // Show saving state if authorization is bypassed
        this.showSavingState();
      }
    } else if (this.eventFormTarget.checkValidity()) {
      // Show saving state for non-therapy events
      this.showSavingState();
    }
  }

  async checkAuthorizationValidation(formData: FormData) {
    const data = await this.makeRequest(this.authorizationVerificationUrlValue, formData);

    if (data?.valid) {
      this.addAuthorizationCheck();

      // Show saving state after successful authorization validation
      this.showSavingState();

      this.submitBtnTarget.click();
    } else {
      this.showAuthorizationWarning(data.authorization_issues.join(' '), data.authorization_issues.length);
      this.removeAuthorizationCheck();
    }
  }

  // eslint-disable-next-line consistent-return
  private async makeRequest(src: string, formData: FormData) {
    formData.delete('_method');
    try {
      const { data } = await axios.post(src, formData, {
        headers: {
          accept: 'application/json',
          credentials: 'same-origin',
          'Content-Type': 'multipart/form-data',
        },
      });

      this.dispatch('success', { bubbles: true });
      return data;
    } catch (error) {
      this.dispatch('error', { bubbles: true });
    }
  }

  removeEmptyKey(formData: FormData, key: string) {
    // Create a new FormData object to store the filtered data
    const newFormData = new FormData();

    // Iterate over the entries in the original FormData object
    for (const [formKey, value] of formData.entries()) {
      // Only append to new FormData if it's not the specified key with an empty value
      if (formKey !== key || (formKey === key && value.trim() !== '')) {
        newFormData.append(formKey, value);
      }
    }

    return newFormData;
  }

  removeAuthorizationCheck() {
    this.authorizationCheckerTarget.removeAttribute('checked');
  }

  addAuthorizationCheck() {
    this.authorizationCheckerTarget.setAttribute('checked', '1');
  }

  declare readonly startTimeValue: string;
  declare readonly authorizationVerificationUrlValue: string;
  declare readonly allDayEnabledValue: boolean;
  declare readonly allDayModeCheckboxTarget: HTMLInputElement;
  declare readonly startTimeTimeContainerTarget: HTMLElement;
  declare readonly endTimeDateContainerTarget: HTMLElement;
  declare readonly durationContainerTarget: HTMLElement;
  declare readonly timezoneContainerTarget: HTMLElement;
  declare readonly whenDatePickerTarget: HTMLInputElement;
  declare readonly untilDatePickerTarget: HTMLInputElement;
  declare readonly submitBtnTarget: HTMLInputElement;
  declare readonly hasSubmitBtnTarget: boolean;
  declare readonly authorizationWarningTarget: HTMLElement;
  declare readonly cancelEventCreationTarget: HTMLElement;
  declare readonly authorizationCheckerTarget: HTMLInputElement;
  declare readonly authorizationWarningMessageTarget: HTMLElement;
  declare readonly eventFormTarget: HTMLFormElement;
  declare readonly issuesCountTarget: HTMLElement;
  declare readonly formTypeValue: string;
  declare readonly savingStateTarget: HTMLElement;
  declare readonly eventLocationSelectTarget: EventLocationSelect;
  declare readonly hasEventLocationSelectTarget: boolean;
}

function isRRuleEqual(a: string, b: string) {
  return isEqual(parseJSON(a, a), parseJSON(b, b));
}
